import { type FC } from "react"

import { toDateTimeString } from "@forento/core/utilities/date"
import { toNumberFormat } from "@forento/core/utilities/number"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import LoadingError from "~/components/LoadingError"
import LoadingIndicator from "~/components/LoadingIndicator"
import Table from "~/components/Table"
import { usePlatform } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

const AiCreditHistory: FC = () => {
	const platform = usePlatform()!
	const isEnabled = platform.enabledFeatures.includes("ai-generated-courses")
	const transactions = query.ai.listCreditTransactions.useInfiniteQuery(
		{},
		{ getNextPageParam: page => page.nextCursor, enabled: isEnabled },
	)
	const items = transactions.data?.pages.flatMap(page => page.items) ?? []

	if (!isEnabled) return null

	return (
		<section className="mt-6 flex flex-col gap-3">
			<h2 className="text-lg font-semibold text-gray-950">AI credit history</h2>
			{transactions.error && transactions.data === undefined ? (
				<LoadingError
					error={transactions.error}
					subject="your credit history"
					onRetry={() => transactions.refetch()}
				/>
			) : transactions.isPending ? (
				<LoadingIndicator size={32} className="self-center" />
			) : (
				<Table
					isCompact
					columns={[
						{ label: "Date", flex: { grow: 0, basis: 160 } },
						"Activity",
						{ label: "Credits", flex: { grow: 0, basis: 100 } },
						{ label: "Balance", flex: { grow: 0, basis: 100 } },
					]}
					data={items.map(transaction => [
						toDateTimeString(transaction.createDate),
						<div key="activity" className="flex flex-col gap-0.5">
							<span className="text-gray-950">{transaction.description ?? "Credit adjustment"}</span>
							{transaction.createdByUser !== null && (
								<span className="text-xs text-gray-500">
									By {transaction.createdByUser.firstName} {transaction.createdByUser.lastName}
								</span>
							)}
						</div>,
						<span
							key="amount"
							className={cn("font-medium", transaction.amount > 0 ? "text-green-700" : "text-gray-950")}
						>
							{transaction.amount > 0 ? "+" : ""}
							{toNumberFormat(transaction.amount)}
						</span>,
						toNumberFormat(transaction.balanceAfter),
					])}
					emptyText="No credit activity yet."
				/>
			)}
			{transactions.hasNextPage && (
				<div className="flex flex-col items-center gap-2">
					{transactions.isFetchNextPageError && (
						<p className="text-sm text-red-700">Failed to load more credit history. Please try again.</p>
					)}
					<Button
						variant="secondary"
						isLoading={transactions.isFetchingNextPage}
						onClick={() => void transactions.fetchNextPage()}
					>
						Load more
					</Button>
				</div>
			)}
		</section>
	)
}

export default AiCreditHistory
